// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { xCommonLoadingOverlay } from "@flip/App/Handlers/XNG/Common";
import { CustomAppState } from "@flip/App/Store/Reducers";
import {
    CheckoutStep,
    CHECKOUT_STEPS,
    nextStepHandler,
    previousStepHandler,
} from "@flip/App/Widgets/CheckoutStepper/CheckoutStepper.helper";
import { StepperContext } from "@flip/App/Widgets/CheckoutStepper/StepperContext";
import { CheckoutShippingFormContext } from "@flip/Overrides/Pages/CheckoutShippingPage";
import { color, doScroll, xScreen } from "@flip/XDL/Utilities";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import {
    canPlaceOrder,
    canSubmitForApprovalOrder,
    getCartState,
    getCurrentCartState,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getQueryStrings } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import setInitialValues from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/SetInitialValues";
import updateCart from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/UpdateCart";
import updateShipTo from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/UpdateShipTo";
import { AnyAction } from "@insite/client-framework/Store/Reducers";
import translate from "@insite/client-framework/Translate";
import { ShipToModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import PlaceOrderButton from "@insite/content-library/Widgets/CheckoutReviewAndSubmit/CheckoutReviewAndSubmitPlaceOrderButton";
import SubmitForApprovalButton from "@insite/content-library/Widgets/CheckoutReviewAndSubmit/CheckoutReviewAndSubmitSubmitForApprovalButton";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import cloneDeep from "lodash/cloneDeep";
import React, { useContext } from "react";
import { connect, ResolveThunks } from "react-redux";
import { Dispatch } from "redux";
import { css } from "styled-components";

interface OwnProps {}

const mapDispatchToProps = {
    xCommonLoadingOverlay,
    updateShipTo,
    setAddress: (address: ShipToModel) => (dispatch: Dispatch<AnyAction>) => {
        dispatch({
            type: "Pages/CheckoutShipping/SetShippingAddress",
            address,
        });
    },
    updateCart: makeHandlerChainAwaitable(updateCart),
    setInitialValues,
    setShouldTrackPayment: () => (dispatch: Dispatch<AnyAction>) => {
        dispatch({
            type: "xGoogleAnalytics/setShouldTrackPayment",
            shouldTrackPayment: true,
        } as any);
    },
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps>;

export interface CheckoutStepperStyles {
    container?: InjectableCss;
    prev?: ButtonPresentationProps;
    next?: ButtonPresentationProps;
}

export const styles: CheckoutStepperStyles = {
    container: {
        css: css`
            display: flex;
            flex-direction: row;
            align-items: center;
            justify-content: space-between;
            width: 100%;
            margin-top: 32px;
            padding-top: 24px;
            border-top: 1px solid ${color("common.border")};
            ${xScreen("lte", "xs")} {
                flex-direction: column-reverse;
                justify-content: center;
                padding-top: 32px;
            }
        `,
    },
    prev: {
        variant: "secondary",
        mergeCss: true,
        css: css`
            ${xScreen("lt", "xs")} {
                margin-top: 32px;
            }
        `,
    },
    next: {
        variant: "primary",
        mergeCss: true,
        css: css`
            line-height: 17px;
            border: 0;
        `,
    },
};

const CheckoutStepper: React.FC<Props> = props => {
    const { currentStep, steps, setCurrentStep, checkoutInfoState } = useContext(StepperContext);
    const { validators } = useContext(CheckoutShippingFormContext);

    const history = useHistory();

    const {
        cartPage,
        checkoutShippingPage,
        isLoading,
        cart,
        showPlaceOrderButton,
        showSubmitForApprovalOrder,
        isChangingAddress,
        carrierAccountNo,
        poNumber,
        lastFour,
        paymentMethod,
        canCheckout,
    } = useAppSelector(state => {
        const { cartId } = state.pages.checkoutReviewAndSubmit;
        const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
        return {
            checkoutShippingPage: getPageLinkByPageType(state, "CheckoutShippingPage"),
            cartPage: getPageLinkByPageType(state, "CartPage"),
            isLoading: cartState.isLoading,
            cart: cartState.value,
            showPlaceOrderButton: canPlaceOrder(cartState.value),
            showSubmitForApprovalOrder: canSubmitForApprovalOrder(getCurrentCartState(state).value),
            isChangingAddress: (state as CustomAppState).xCheckout.isChangingShipToAddress,
            carrierAccountNo: (state as CustomAppState).xCheckout.carrierAccountNo,
            poNumber: (state as CustomAppState).xCheckout?.payment.poNumber,
            lastFour: (state as CustomAppState).xCheckout?.payment.lastFour,
            paymentMethod: (state as CustomAppState).xCheckout?.payment.paymentMethod,
            canCheckout: cartState.value?.canCheckOut,
        };
    });

    const goToNextStep = async (nextStep: CheckoutStep) => {
        if (currentStep === CHECKOUT_STEPS.SHIPPING && carrierAccountNo) {
            await props.updateCart({ fullExpand: true });
        }

        if (currentStep !== CHECKOUT_STEPS.ADDRESS) {
            setCurrentStep(nextStep.index);
        }

        if (currentStep === CHECKOUT_STEPS.PAYMENT) {
            props.setShouldTrackPayment();
        }

        doScroll(true);
    };

    const goToPrevStep = (prevStep: CheckoutStep) => {
        if (currentStep === CHECKOUT_STEPS.ADDRESS) {
            history.push(cartPage?.url as string);
        } else if (prevStep.index === CHECKOUT_STEPS.ADDRESS) {
            history.push(checkoutShippingPage?.url as string);
        } else {
            setCurrentStep(prevStep.index);
        }
        doScroll(true);
    };

    const prevStep = previousStepHandler(steps, currentStep);
    const nextStep = nextStepHandler(steps, currentStep);

    const isNextButtonDisabled = () => {
        if (currentStep < 0 || isLoading || !canCheckout) {
            return true;
        }

        if (currentStep === CHECKOUT_STEPS.ADDRESS && isChangingAddress) {
            return true;
        }

        if (currentStep === CHECKOUT_STEPS.PAYMENT) {
            if (cart?.requiresPoNumber && cart?.showPoNumber && (poNumber === undefined || poNumber.trim() === "")) {
                return true;
            }

            if (
                !cart?.paymentOptions?.paymentMethods ||
                cart?.paymentOptions?.paymentMethods.length === 0 ||
                paymentMethod === undefined ||
                paymentMethod.trim() === ""
            ) {
                return true;
            }

            if (cart?.paymentMethod?.isCreditCard && !cart?.requiresApproval && !lastFour) {
                return true;
            }
        }

        return false;
    };

    return (
        <StyledWrapper {...styles.container}>
            <Button {...styles.prev} onClick={() => goToPrevStep(prevStep)} disabled={isLoading} type="button">
                {`${currentStep === CHECKOUT_STEPS.ADDRESS ? translate("Back to Cart") : translate("Go Back")}`}
            </Button>

            {currentStep !== CHECKOUT_STEPS.REVIEW && (
                <Button
                    {...styles.next}
                    onClick={() => goToNextStep(nextStep)}
                    disabled={isNextButtonDisabled()}
                    type={currentStep === CHECKOUT_STEPS.ADDRESS ? "submit" : "button"}
                >
                    {`${translate("Continue to")} ${nextStep.name}`}
                </Button>
            )}
            {currentStep === CHECKOUT_STEPS.REVIEW && cart && showPlaceOrderButton && (
                <PlaceOrderButton styles={styles.next} />
            )}
            {currentStep === CHECKOUT_STEPS.REVIEW && cart && showSubmitForApprovalOrder && (
                <SubmitForApprovalButton extendedStyles={styles.next} />
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(null, mapDispatchToProps)(CheckoutStepper),
    definition: {
        group: "Checkout - Shipping",
        allowedContexts: ["CheckoutShippingPage", "CheckoutReviewAndSubmitPage"],
        displayName: "Checkout Stepper Actions",
    },
};

export default widgetModule;
